import VbivStatus from '@/components/vbiv-status';
import { useOpenClient } from '@/hooks/use-open-client';
import type { IPush } from '@/entities/push.entity';
import { cn } from '@/lib/utils';
import { BellIcon, CalendarClockIcon, ContainerIcon, HashIcon } from 'lucide-react';
import { memo, useCallback, type MouseEvent } from 'react';
import { toast } from 'sonner';
import TimeAgo from 'react-timeago';
import russianStrings from 'react-timeago/lib/language-strings/ru';
import buildFormatter from 'react-timeago/lib/formatters/buildFormatter';

const formatter = buildFormatter(russianStrings);

const PushCard = memo(function PushCard({ push }: { push: IPush }) {
	const { openClient } = useOpenClient();
	const goToClient = useCallback(() => {
		openClient(push.clientId, { listType: 'pushes', clientId: push.clientId, itemId: push.id });
	}, [openClient, push.clientId, push.id]);

	function handleCopy(e: MouseEvent, text: string) {
		e.stopPropagation();
		navigator.clipboard
			.writeText(text)
			.then(() => toast.success('Скопировано'))
			.catch(() => toast.error('Ошибка копирования'));
	}

	return (
		<div
			role='button'
			tabIndex={0}
			className='relative p-2 bg-card rounded-md flex flex-col gap-1.5 border-border border border-l-2 border-l-chart-4 overflow-hidden cursor-pointer active:bg-accent/40'
			onClick={goToClient}
			onKeyDown={e => {
				if (e.key === 'Enter' || e.key === ' ') {
					e.preventDefault();
					goToClient();
				}
			}}
		>
			<div className='flex justify-between items-center'>
				<span className='flex gap-1 items-center min-w-0'>
					<div className='w-[18px] shrink-0'>
						<BellIcon size={16} className='text-chart-4 opacity-80' />
					</div>
					<button
						type='button'
						className='text-sm font-semibold text-chart-4 hover:bg-accent rounded-xs px-0.5 cursor-pointer select-none truncate'
						onClick={e => handleCopy(e, push.packageName)}
					>
						{push.packageName}
					</button>
				</span>
				<div onClick={e => e.stopPropagation()}>
					<VbivStatus clientId={push.clientId} takeStatus={push.takeStatus} takenFrom={push.takenFrom} />
				</div>
			</div>

			{push.comment ? (
				<span className='text-sm text-[#e89bff] truncate' onClick={e => handleCopy(e, push.comment || '')}>
					{push.comment}
				</span>
			) : null}

			<div className='flex justify-between'>
				<span className='flex gap-1 items-center break-all'>
					<div className='w-[18px]'>
						<HashIcon size={16} className='opacity-40' />
					</div>
					<span className='text-sm hover:bg-accent cursor-pointer select-none' onClick={e => handleCopy(e, '#' + push.clientId)}>
						{push.clientId}
					</span>
				</span>
			</div>

			{push.buildId ? (
				<div className='flex justify-between'>
					<span className='flex gap-1 items-center'>
						<div className='w-[18px]'>
							<ContainerIcon size={16} className='opacity-40' />
						</div>
						<span className='text-sm hover:bg-accent cursor-pointer select-none' onClick={e => handleCopy(e, push.buildId)}>
							{push.buildId}
						</span>
					</span>
				</div>
			) : null}

			<button
				type='button'
				className={cn(
					'block w-full text-left rounded-2xl rounded-tl-sm bg-muted/50 px-2.5 py-2 hover:bg-muted/70 cursor-pointer select-none',
				)}
				onClick={e => handleCopy(e, `${push.title}\n${push.text}`)}
			>
				{push.title ? <p className='text-sm font-medium leading-snug'>{push.title}</p> : null}
				{push.text ? (
					<p className={cn('text-sm leading-relaxed text-muted-foreground', push.title && 'mt-1')}>{push.text}</p>
				) : null}
			</button>

			<span className='flex gap-1 items-center'>
				<div className='w-[18px]'>
					<CalendarClockIcon size={16} className='opacity-40' />
				</div>
				<span className='text-sm font-semibold opacity-40'>Получен</span>
				<div className='text-sm text-chart-4 opacity-95'>
					<TimeAgo date={push.createdAt} formatter={formatter} />
				</div>
			</span>
		</div>
	);
});

export default PushCard;
